import {
  Box,
  Button,
  FormControl,
  Grid,
  Modal,
  Typography,
  TextField,
  Autocomplete,
} from '@mui/material'
import { createFilterOptions } from '@mui/material/Autocomplete'
import React from 'react'
import { FormikProps } from 'formik'

interface DealerGroupData {
  dealerGroupNumber: string
  dealerIdList: { dealerId: string }[]
}

interface FormValues {
  email: string
  firstName: string
  lastName: string
  phone: string
  dealerGroupData: DealerGroupData[]
  aftermarketClient: string[]
}

interface Props {
  openAddDealerGroup: boolean
  onCloseAddDealerGroup: () => void
  formik: FormikProps<FormValues>
  dealerGroupNumberList: {
    dealerGroupName: string
    dealerGroupNumber: string
    dealerGroup: string
  }[]
}

const styles = {
  container: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '12px',
  },
  headerStyle: {
    borderRadius: '12px',
    display: 'flex',
    p: 2,
    justifyContent: 'center',
    backgroundColor: 'white',
    mb: 4,
  },
  selectMenu: {
    maxHeight: 200,
    marginTop: '8px',
  },
  formControlStyles: {
    width: '90%',
    margin: 'auto',
  },
}

const filter = createFilterOptions<Props['dealerGroupNumberList'][number]>({
  stringify: (option) =>
    option.dealerGroupName || option.dealerGroup || option.dealerGroupNumber || '',
})

function UserDealerGroupAdd(props: Props) {
  const { openAddDealerGroup, onCloseAddDealerGroup, formik, dealerGroupNumberList } = props

  const selectedDealerGroupNumbers = formik.values.dealerGroupData.map((g) => g.dealerGroupNumber)

  // remove duplicates
  const uniqueDealerGroups = dealerGroupNumberList.filter(
    (value, index, self) =>
      self.findIndex((t) => t.dealerGroupNumber === value.dealerGroupNumber) === index,
  )

  // sort alphabetically
  const sortedDealerGroups = [...uniqueDealerGroups].sort((a, b) => {
    const labelA = a.dealerGroupName || a.dealerGroup || a.dealerGroupNumber || ''
    const labelB = b.dealerGroupName || b.dealerGroup || b.dealerGroupNumber || ''
    return labelA.toLowerCase().localeCompare(labelB.toLowerCase())
  })

  return (
    <Modal
      open={openAddDealerGroup}
      onClose={onCloseAddDealerGroup}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Box sx={styles.container}>
        <Box sx={styles.headerStyle}>
          <Typography id='modal-modal-title' variant='h5' component='h2' color='black'>
            Dealer Groups
          </Typography>
        </Box>

        <Grid xs={12}>
          <Grid item sx={styles.formControlStyles}>
            <FormControl fullWidth>
              <Autocomplete
                multiple
                id='dealer-group-autocomplete'
                options={sortedDealerGroups}
                disableCloseOnSelect
                filterOptions={filter}
                filterSelectedOptions
                getOptionLabel={(option) =>
                  option.dealerGroupName || option.dealerGroup || option.dealerGroupNumber || ''
                }
                isOptionEqualToValue={(option, value) =>
                  option.dealerGroupNumber === value.dealerGroupNumber
                }
                value={selectedDealerGroupNumbers.map(
                  (num) =>
                    sortedDealerGroups.find((g) => g.dealerGroupNumber === num) || {
                      dealerGroupNumber: num,
                      dealerGroup: num,
                      dealerGroupName: '',
                    },
                )}
                onChange={(event, newValue) => {
                  const sortedValues = [...newValue].sort((a, b) => {
                    const labelA = a.dealerGroupName || a.dealerGroup || a.dealerGroupNumber || ''
                    const labelB = b.dealerGroupName || b.dealerGroup || b.dealerGroupNumber || ''
                    return labelA.toLowerCase().localeCompare(labelB.toLowerCase())
                  })

                  const updatedDealerGroupData = sortedValues.map((group) => {
                    const existingGroup = formik.values.dealerGroupData.find(
                      (d) => d.dealerGroupNumber === group.dealerGroupNumber,
                    )

                    return (
                      existingGroup || {
                        dealerGroupNumber: group.dealerGroupNumber,
                        dealerIdList: [],
                      }
                    )
                  })

                  formik.setFieldValue('dealerGroupData', updatedDealerGroupData)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant='outlined'
                    label='Dealer Group Name'
                    placeholder='Search Dealer Groups...'
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: 1,
                    }}
                  />
                )}
                ListboxProps={{ style: styles.selectMenu }}
              />
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ textAlign: 'right', mt: '30px', mb: '30px', mr: '20px' }}>
          <Button
            variant='outlined'
            size='large'
            sx={{ mr: 2, borderRadius: '20px' }}
            color='error'
            onClick={onCloseAddDealerGroup}
          >
            Cancel
          </Button>

          <Button
            onClick={onCloseAddDealerGroup}
            variant='contained'
            sx={{ borderRadius: '20px' }}
            size='large'
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default UserDealerGroupAdd
